import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, X, Send, User, Mic, Square, Volume2 } from "lucide-react";
import { toast } from "sonner";
import AiActionCard, { executeAction } from "@/components/AiActionCard";

const MODEL_OPTIONS = [
  { value: "gpt-5.4", label: "GPT-5.4" },
  { value: "gpt-5.4-mini", label: "GPT-5.4 Mini" },
  { value: "gpt-5.5", label: "GPT-5.5" },
  { value: "gpt-5.6-sol", label: "GPT-5.6 Sol" },
  { value: "claude-sonnet-5", label: "Claude Sonnet 5" },
  { value: "claude-opus-4-8", label: "Claude Opus 4.8" },
  { value: "gemini-3.1-pro", label: "Gemini 3.1 Pro" },
  { value: "gemini-3.5-flash", label: "Gemini 3.5 Flash" },
];

const GUEST_SUGGESTIONS = {
  fr: ["C'est quoi AgriNova ?", "Comment passer commande ?", "Quels sont les tarifs ?"],
  en: ["What is AgriNova?", "How do I place an order?", "What are the prices?"],
};

const MEMBER_SUGGESTIONS = {
  fr: ["Quels bassins sont actifs ?", "Quelles récoltes arrivent dans 30 jours ?", "Quels stocks risquent la rupture ?"],
  en: ["Which ponds are active?", "Which harvests are due in 30 days?", "Which stocks are running low?"],
};

// Suggestions contextuelles selon la page visitée (fallback = profil)
const PAGE_SUGGESTIONS = {
  "/commander": {
    fr: ["Quels produits sont disponibles ?", "Comment suivre ma commande ?", "Quels moyens de paiement acceptez-vous ?"],
    en: ["What products are available?", "How do I track my order?", "What payment methods do you accept?"],
  },
  "/pisciculture": {
    fr: ["Comment améliorer mon FCR ?", "Quelle densité pour le tilapia ?", "Comment surveiller la qualité de l'eau ?"],
    en: ["How do I improve my FCR?", "What stocking density for tilapia?", "How do I monitor water quality?"],
  },
  "/parcelles": {
    fr: ["Quelles récoltes arrivent dans 30 jours ?", "Quelle culture pour cette saison ?", "Comment optimiser mes rendements ?"],
    en: ["Which harvests are due in 30 days?", "Which crop for this season?", "How do I optimize my yields?"],
  },
  "/fournisseurs": {
    fr: ["Quels stocks risquent la rupture ?", "Comment comparer mes fournisseurs ?", "Quand commander l'aliment poisson ?"],
    en: ["Which stocks are running low?", "How do I compare my suppliers?", "When should I order fish feed?"],
  },
  "/clients": {
    fr: ["Quelles commandes sont en retard ?", "Comment fidéliser mes clients ?", "Quel est mon meilleur produit ?"],
    en: ["Which orders are late?", "How do I retain my customers?", "What is my best product?"],
  },
};

export default function AiChatWidget() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("gpt-5.4");
  const [showWelcome, setShowWelcome] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [playingIdx, setPlayingIdx] = useState(-1);
  const [voiceOn, setVoiceOn] = useState(false);
  const voiceRef = useRef(false);
  const mediaRef = useRef(null);
  const bottomRef = useRef(null);

  async function speakAndContinue(text) {
    setPlayingIdx(-2);
    try {
      const { data } = await api.post("/ai/tts", { text: text.slice(0, 1500) }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      await new Promise((resolve) => {
        const audio = new Audio(url);
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
    setPlayingIdx(-1);
    if (voiceRef.current) startRecording();  // boucle mains libres : réécoute automatique
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks, { type: "audio/webm" }), "question.webm");
          const { data } = await api.post(`/ai/stt?lang=${lang}`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) {
            setInput("");
            await ask({ preventDefault: () => {} }, data.text.trim());  // envoi auto en mode voix
          }
        } catch {
          toast.error(t("error"));
        }
        setTranscribing(false);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      voiceRef.current = false;
      setVoiceOn(false);
      toast.error(t("ai_mic_error"));
    }
  }

  function toggleVoice() {
    if (voiceOn) {
      voiceRef.current = false;
      setVoiceOn(false);
      mediaRef.current?.stop();
      setRecording(false);
    } else {
      voiceRef.current = true;
      setVoiceOn(true);
      startRecording();
    }
  }

  async function playTTS(text, i) {
    if (playingIdx >= 0) return;
    setPlayingIdx(i);
    try {
      const { data } = await api.post("/ai/tts", { text: text.slice(0, 1500) }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audio.onended = () => { setPlayingIdx(-1); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlayingIdx(-1); URL.revokeObjectURL(url); };
      await audio.play();
    } catch {
      setPlayingIdx(-1);
      toast.error(t("error"));
    }
  }

  // Message d'accueil proactif au premier chargement (une fois par session)
  useEffect(() => {
    if (sessionStorage.getItem("ai_welcomed")) return;
    const timer = setTimeout(() => setShowWelcome(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  function toggleWidget() {
    setShowWelcome(false);
    sessionStorage.setItem("ai_welcomed", "1");
    if (open) {
      voiceRef.current = false;
      setVoiceOn(false);
      mediaRef.current?.stop();
      setRecording(false);
    }
    setOpen((o) => !o);
  }

  useEffect(() => {
    if (!open || !user) return;
    api.get("/ai/history").then((r) => {
      setMessages(r.data.slice(-30).map((m) => ({ role: m.role, content: m.content, model: m.model })));
      if (user.role === "invité") return;
      const today = new Date().toISOString().slice(0, 10);
      // Résumé matinal — une fois par jour, TOUT EN HAUT (inséré après les alertes)
      const loadBrief = () => {
        if (localStorage.getItem("ai_brief_date") !== today) {
          api.get(`/ai/morning-brief?lang=${lang}`).then((b) => {
            if (b.data?.message) {
              localStorage.setItem("ai_brief_date", today);
              setMessages((m) => [{ role: "assistant", content: b.data.message }, ...m]);
            }
          }).catch(() => {});
        }
      };
      // Récap hebdomadaire — le lundi uniquement, sous le résumé matinal
      const loadRecap = () => {
        const now = new Date();
        const week = `${now.getFullYear()}-W${Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / 604800000)}`;
        if (now.getDay() === 1 && localStorage.getItem("ai_recap_week") !== week) {
          api.get(`/ai/weekly-recap?lang=${lang}`).then((r) => {
            if (r.data?.message) {
              localStorage.setItem("ai_recap_week", week);
              setMessages((m) => [{ role: "assistant", content: r.data.message }, ...m]);
            }
          }).catch(() => {}).finally(loadBrief);
        } else {
          loadBrief();
        }
      };
      // Alertes proactives (membres) — une fois par 6 h, sous le résumé
      const last = Number(sessionStorage.getItem("ai_proactive_at") || 0);
      if (Date.now() - last > 6 * 3600 * 1000) {
        api.get("/ai/proactive").then((p) => {
          if (p.data.length) {
            sessionStorage.setItem("ai_proactive_at", String(Date.now()));
            setMessages((m) => [...p.data.map((a) => ({ role: "assistant", content: a.message, action: a.action })), ...m]);
          }
        }).catch(() => {}).finally(loadRecap);
      } else {
        loadRecap();
      }
    }).catch(() => {});
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function ask(e, preset) {
    e.preventDefault();
    const q = (preset || input).trim();
    if (!q || streaming) return;
    setInput("");
    let fullText = "";
    let voiceRecap = "";
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", model }]);
    setStreaming(true);
    if (!user) {
      // Visiteur anonyme : endpoint public (non-streamé, rate-limité, sans données internes)
      try {
        const { data } = await api.post("/public/ai/chat", { question: q, lang });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: data.answer };
          return copy;
        });
      } catch (err) {
        const detail = err.response?.data?.detail;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: `[${typeof detail === "string" ? detail : t("error")}]` };
          return copy;
        });
      }
      setStreaming(false);
      return;
    }
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("agrinova_token");
      const resp = await fetch(`${API}/api/ai/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ question: q, lang, model }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ") || part.includes("[DONE]")) continue;
          try {
            const ev = JSON.parse(part.slice(6));
            if (ev.action) {
              // Garde-fou vocal : seules les créations/lectures passent en direct ;
              // toute action sensible (suppression, paiement…) exigera toujours la carte.
              const SAFE_DIRECT = ["create_task", "create_feeding", "create_supplier", "create_customer",
                "create_purchase", "create_health", "send_reminder", "complete_task", "log_measurement", "generate_invoice"];
              if (voiceRef.current && SAFE_DIRECT.includes(ev.action.type)) {
                // Commande vocale directe : exécution immédiate sans carte de confirmation
                const r = await executeAction(ev.action, ev.action, t, lang);
                voiceRecap = r.ok
                  ? r.recap
                  : (lang === "en" ? `Sorry, the action failed: ${r.error}` : `Désolé, l'action a échoué : ${r.error}`);
                setMessages((m) => [...m, { role: "assistant", content: voiceRecap }]);
              } else {
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { ...copy[copy.length - 1], action: ev.action };
                  return copy;
                });
              }
            }
            if (ev.delta) {
              fullText += ev.delta;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.delta };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: `[${t("error")}]` };
        return copy;
      });
    }
    if (voiceRef.current && (voiceRecap || fullText)) await speakAndContinue(voiceRecap || fullText);  // lecture du récapitulatif puis réécoute
    setStreaming(false);
  }

  if (location.pathname === "/assistant") return null;

  return (
    <>
      {showWelcome && !open && (
        <button onClick={toggleWidget} data-testid="ai-widget-welcome"
          className="fixed bottom-24 right-5 z-50 max-w-[240px] rounded-2xl rounded-br-sm border border-border bg-card px-4 py-3 text-sm text-left shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-300">
          {t("ai_welcome")}
        </button>
      )}
      <button onClick={toggleWidget} data-testid="ai-widget-button" aria-label={t("ai_assistant")}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[60vh] max-h-[540px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-widget-panel">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/50">
            <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
            <div className="font-semibold text-sm flex-1">{t("ai_assistant")}</div>
            {user && (
            <Select value={model} onValueChange={setModel} disabled={streaming}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="ai-widget-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODEL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`ai-widget-model-${o.value}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-6 space-y-3">
                <div className="text-muted-foreground text-xs">{t("ai_empty")}</div>
                <div className="flex flex-col gap-2" data-testid="ai-widget-suggestions">
                  {(PAGE_SUGGESTIONS[location.pathname] || (user ? MEMBER_SUGGESTIONS : GUEST_SUGGESTIONS))[lang === "en" ? "en" : "fr"].map((s, i) => (
                    <button key={s} type="button" onClick={(e) => ask(e, s)} disabled={streaming}
                      className="text-xs text-left rounded-lg border border-border bg-background px-3 py-2 hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-50"
                      data-testid={`ai-widget-suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="w-6 h-6 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Bot className="w-3.5 h-3.5" />
                  </div>
                )}
                <div className="max-w-[80%] space-y-1.5">
                  <div className={`rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`} data-testid={`ai-widget-msg-${i}`}>
                    {(m.content || "").split("ACTION_JSON:")[0] || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                  {m.role === "assistant" && m.content && (
                    <button onClick={() => playTTS(m.content, i)} data-testid={`ai-widget-tts-${i}`}
                      title={t("ai_listen")} disabled={playingIdx >= 0}
                      className="self-start text-muted-foreground hover:text-primary transition-colors disabled:opacity-40">
                      <Volume2 className={`w-4 h-4 ${playingIdx === i ? "animate-pulse text-primary" : ""}`} />
                    </button>
                  )}
                  {m.action && <AiActionCard action={m.action} />}
                </div>
                {m.role === "user" && (
                  <div className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center shrink-0">
                    <User className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={ask} className="p-3 border-t border-border flex gap-2">
            {user && (
            <button type="button" onClick={toggleVoice} disabled={transcribing}
              data-testid="ai-widget-mic" title={t("ai_voice_continuous")}
              className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors disabled:opacity-50 ${
                voiceOn ? "bg-destructive text-destructive-foreground animate-pulse" : "border border-border hover:bg-secondary"
              }`}>
              {voiceOn ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            )}
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={transcribing ? t("ai_transcribing") : t("ask_question")}
              className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="ai-widget-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} data-testid="ai-widget-send"
              className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
